import {
  Component,
  computed,
  inject,
  OnDestroy,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppFooter } from './app.footer';
import { LayoutService } from '@/core/layout/service/layout.service';
import { AppBreadcrumb } from './app.breadcrumb';
import { AppSidebar } from './app.sidebar';
import { AppMastheadContainer } from './app-masthead/app-masthead.container';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ServiceHealthService } from '@/core/services/service-health.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    AppTopbar,
    AppSidebar,
    RouterModule,
    AppFooter,
    AppBreadcrumb,
    AppMastheadContainer,
    Toast,
    ButtonModule,
  ],
  template: `
    <div class="layout-container" [ngClass]="containerClass()">
      <div app-topbar></div>
      <div app-sidebar></div>
      <div class="layout-content-wrapper">
        <div app-breadcrumb></div>
        <app-masthead-container />
        <div class="layout-content">
          <router-outlet></router-outlet>
        </div>
        <div app-footer></div>
      </div>
    </div>
    <p-toast />
    <p-toast key="serviceError" position="top-right">
      <ng-template pTemplate="message" let-message>
        <div class="flex flex-col gap-3 w-full">
          <div class="flex items-start gap-3">
            <i
              class="pi"
              [ngClass]="{
                'pi-exclamation-triangle text-orange-500': message.severity === 'warn',
                'pi-times-circle text-red-500': message.severity === 'error'
              }"
              style="font-size: 1.5rem"
            ></i>
            <div class="flex-1">
              <div class="font-semibold text-surface-900">{{ message.summary }}</div>
              <div class="text-sm text-surface-600 mt-1">{{ message.detail }}</div>
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <button
              pButton
              label="Dismiss"
              class="p-button-text p-button-sm"
              (click)="onDismissServiceError()"
            ></button>
            <button
              pButton
              label="Retry"
              icon="pi pi-refresh"
              class="p-button-sm"
              (click)="onRetryServiceError()"
            ></button>
          </div>
        </div>
      </ng-template>
    </p-toast>
  `,
  // No MessageService provider here on purpose: these <p-toast> outlets are the
  // app-wide sink for the ROOT MessageService, which root-provided stores and
  // the http-error interceptor inject. A component-level provider would shadow
  // it and silently swallow their toasts. Components with their own <p-toast>
  // must provide their own MessageService to stay isolated from this one.
})
export class AppLayout implements OnDestroy {
  private serviceHealth = inject(ServiceHealthService);
  private messageService = inject(MessageService);
  private http = inject(HttpClient);

  overlayMenuOpenSubscription: Subscription;

  menuOutsideClickListener: any;

  menuScrollListener: any;

  @ViewChild(AppSidebar) appSidebar!: AppSidebar;

  @ViewChild(AppTopbar) appTopbar!: AppTopbar;

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router
  ) {
    this.overlayMenuOpenSubscription =
      this.layoutService.overlayOpen$.subscribe(() => {
        if (!this.menuOutsideClickListener) {
          this.menuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appSidebar.appMenu.el.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appSidebar.appMenu.el.nativeElement.contains(
                  event.target
                ) ||
                this.appTopbar.menuButton.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appTopbar.menuButton.nativeElement.contains(event.target)
              );
              if (isOutsideClicked) {
                this.hideMenu();
              }
            }
          );
        }

        if (
          (this.layoutService.isSlim() || this.layoutService.isSlimPlus()) &&
          !this.menuScrollListener
        ) {
          this.menuScrollListener = this.renderer.listen(
            this.appSidebar.appMenu.menuContainer.nativeElement,
            'scroll',
            (_event) => {
              if (this.layoutService.isDesktop()) {
                this.hideMenu();
              }
            }
          );
        }

        if (this.layoutService.layoutState().staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.hideMenu());
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      );
    }
  }

  hideMenu() {
    console.log('hideMenu!');
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    this.layoutService.reset();
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  containerClass = computed(() => {
    const layoutConfig = this.layoutService.layoutConfig();
    const layoutState = this.layoutService.layoutState();

    console.log('mobile active', layoutState.staticMenuMobileActive);
    return {
      'layout-overlay': layoutConfig.menuMode === 'overlay',
      'layout-static': layoutConfig.menuMode === 'static',
      'layout-slim': layoutConfig.menuMode === 'slim',
      'layout-slim-plus': layoutConfig.menuMode === 'slim-plus',
      'layout-horizontal': layoutConfig.menuMode === 'horizontal',
      'layout-reveal': layoutConfig.menuMode === 'reveal',
      'layout-drawer': layoutConfig.menuMode === 'drawer',
      'layout-sidebar-dark': layoutConfig.darkTheme,
      'layout-static-inactive':
        layoutState.staticMenuDesktopInactive &&
        layoutConfig.menuMode === 'static',
      'layout-overlay-active': layoutState.overlayMenuActive,
      'layout-mobile-active': layoutState.staticMenuMobileActive,
      'layout-topbar-menu-active': layoutState.topbarMenuActive,
      'layout-sidebar-active': layoutState.sidebarActive,
      'layout-sidebar-anchored': layoutState.anchored,
      [`layout-topbar-${layoutConfig.topbarTheme}`]: true,
      [`layout-menu-${layoutConfig.menuTheme}`]: true,
      [`layout-menu-profile-${layoutConfig.menuProfilePosition}`]: true,
    };
  });

  /**
   * Dismiss the service error toast.
   */
  onDismissServiceError(): void {
    this.messageService.clear('serviceError');
    this.serviceHealth.clearLastFailedRequest();
  }

  /**
   * Retry the last failed request.
   */
  async onRetryServiceError(): Promise<void> {
    const failedRequest = this.serviceHealth.getLastFailedRequest();

    if (!failedRequest) {
      this.messageService.clear('serviceError');
      return;
    }

    try {
      // Replay the failed request
      const req = failedRequest.request;
      await firstValueFrom(
        this.http.request(req.method, req.url, {
          body: req.body,
          headers: req.headers,
          params: req.params,
          responseType: req.responseType as 'json',
          withCredentials: req.withCredentials,
        })
      );

      // Success - clear toast and reset state
      this.messageService.clear('serviceError');
      this.serviceHealth.clearLastFailedRequest();

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Request completed successfully.',
        life: 3000,
      });
    } catch {
      // Still failing - update toast message
      this.messageService.clear('serviceError');
      this.messageService.add({
        key: 'serviceError',
        severity: 'error',
        summary: 'Still Unavailable',
        detail: 'The service is still unavailable. Please try again later.',
        sticky: true,
        closable: true,
      });
    }
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
